import React, { useLayoutEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { download_image } from '../../actions/imageActions';
import { Icons } from './Icons';

const NewImageWindow = ({ children, onClose }) => {
	const containerRef = useRef(null);
	if (!containerRef.current) {
		const c = document.createElement('div');
		c.setAttribute('style', 'text-align:center; margin:0; padding:0;');
		containerRef.current = c;
	}

	useLayoutEffect(() => {
		const external = window.open('', '_blank');
		if (!external) {
			onClose?.();
			return undefined;
		}
		external.document.body.style.margin = '0';
		external.document.body.appendChild(containerRef.current);
		external.addEventListener('beforeunload', () => onClose?.());
		return () => external.close();
	}, []);

	return createPortal(children, containerRef.current);
};

NewImageWindow.propTypes = {
	children: PropTypes.node,
	onClose: PropTypes.func,
};

const PictureButtons = ({ imageInfo, imageSrc, onFit, onShowControls }) => {
	const { t } = useTranslation();
	const { image_id } = useParams();
	const dispatch = useDispatch();
	const [fullscreenOpen, setFullscreenOpen] = useState(false);

	return (
		<>
			<div className='dn-picbtns' role='group' aria-label='Image controls'>
				{onFit && (
					<button
						type='button'
						className='dn-picbtn'
						title={t('display.actionBtns.fitImg', { defaultValue: 'Fit image' })}
						onClick={onFit}
					>
						<Icons.fit />
					</button>
				)}
				<button
					type='button'
					className='dn-picbtn'
					title={t('display.actionBtns.fullScreen', { defaultValue: 'Open in new window' })}
					onClick={() => setFullscreenOpen(true)}
				>
					<Icons.fullscreen />
				</button>
				<button
					type='button'
					className='dn-picbtn'
					title={t('display.actionBtns.download', { defaultValue: 'Download image' })}
					onClick={() => dispatch(download_image(image_id, imageInfo))}
				>
					<Icons.download />
				</button>
				<button
					type='button'
					className='dn-picbtn'
					title={t('display.actionBtns.controls', { defaultValue: 'Keyboard shortcuts' })}
					onClick={onShowControls}
				>
					<Icons.keyboard />
				</button>
			</div>
			{fullscreenOpen && (
				<NewImageWindow onClose={() => setFullscreenOpen(false)}>
					<img src={imageSrc} alt={imageInfo?.filename || ''} style={{ maxWidth: '100%', height: 'auto' }} />
				</NewImageWindow>
			)}
		</>
	);
};

PictureButtons.propTypes = {
	imageInfo: PropTypes.object.isRequired,
	imageSrc: PropTypes.string.isRequired,
	onFit: PropTypes.func,
	onShowControls: PropTypes.func,
};

export default PictureButtons;
